type NetworkBreadcrumb = {
  category?: unknown;
  level?: unknown;
  message?: unknown;
  data?: {
    url?: unknown;
  };
};

type StackFrame = {
  abs_path?: unknown;
  colno?: unknown;
  filename?: unknown;
  function?: unknown;
  lineno?: unknown;
  module?: unknown;
  package?: unknown;
};

// Next.js serves all of our client code from /_next/static/; Sentry rewrites its origin to app://.
const FIRST_PARTY_FRAME_FILENAME = /^(?:[a-z][a-z\d+.-]*:\/\/[^/]*)?\/_next\/static\//i;

type StackErrorInput = {
  exceptionType: unknown;
  exceptionValue: unknown;
  frames?: readonly StackFrame[];
};

type TranslatedDomRemovalErrorInput = StackErrorInput & {
  breadcrumbs?: readonly NetworkBreadcrumb[];
  // Chrome's built-in translation marks <html> with translated-ltr/translated-rtl.
  pageIsTranslated?: boolean;
};

type GoogleAdUndefinedPromiseRejectionInput = {
  breadcrumbs?: readonly NetworkBreadcrumb[];
  exceptionType: unknown;
  exceptionValue: unknown;
  mechanism: unknown;
};

type GoogleTranslatePromiseRejectionInput = StackErrorInput & {
  mechanism: unknown;
  userAgent: unknown;
};

const BROWSER_EXTENSION_TIMEOUT_TARGETS = [
  'lettersvoicesdistributor.getvalue',
  'appsettingsdistributor.getvalue',
  'defineexpirationforlanguagepacks.getvalue',
  'ismathocravailable',
  'userdistributor.getvalue',
  'getdictionariesbylanguageid',
  'speechvoicesdistributor.getvalue',
  'ispredictionavailable',
  'isdictateavailable',
  'dictionariesdistributor.getvalue',
  'getselectedtext',
  'getselectedtextandpreparehighlight',
] as const;
const BROWSER_EXTENSION_API_ERRORS = new Set([
  'invalid call to runtime.sendmessage(). tab not found.',
  'invalid call to browser.storage.local.set(). exceeded storage quota.',
  'wkwebview api client did not respond to this postmessage',
  'wallet must has at least one account',
]);
const MAPBOX_ERROR_CALLBACK_TYPE_ERROR_VALUE = 'this.errorcb is not a function';
const MAPBOX_FRAME_SIGNAL = 'mapbox-gl';
const TRANSLATED_DOM_REMOVAL_ERROR_VALUE = 'the object can not be found here.';
const CHROMIUM_DOM_REMOVAL_ERROR_VALUE =
  "failed to execute 'removechild' on 'node': the node to be removed is not a child of this node.";
const GOOGLE_AD_UNDEFINED_REJECTION_VALUE =
  'non-error promise rejection captured with value: undefined';
const GOOGLE_AD_READY_BREADCRUMB = 'adbanner googletag ready, defining slot';

function lower(value: unknown): string {
  return String(value ?? '').toLowerCase();
}

function normalized(value: unknown): string {
  return lower(value).trim();
}

function isGoogleAdRequest(value: unknown): boolean {
  if (typeof value !== 'string') return false;

  try {
    const url = new URL(value);

    return url.hostname === 'pagead2.googlesyndication.com' && url.pathname === '/gampad/ads';
  } catch {
    return false;
  }
}

export function isBrowserExtensionTimeout(errorTexts: readonly unknown[]): boolean {
  return errorTexts.some((value) => {
    const text = normalized(value);

    return BROWSER_EXTENSION_TIMEOUT_TARGETS.some((target) =>
      text.endsWith(`request timeout ${target}`),
    );
  });
}

export function isBrowserExtensionApiError(errorTexts: readonly unknown[]): boolean {
  return errorTexts.some((value) => BROWSER_EXTENSION_API_ERRORS.has(normalized(value)));
}

export function isGoogleTranslatePromiseRejection({
  exceptionType,
  exceptionValue,
  frames = [],
  mechanism,
  userAgent,
}: GoogleTranslatePromiseRejectionInput): boolean {
  // Google Translate replaces translateHtml failures with Error("La"). Only
  // suppress the observed Chrome iOS signature when there is no source stack.
  if (
    exceptionType !== 'Error' ||
    exceptionValue !== 'La' ||
    mechanism !== 'auto.browser.global_handlers.onunhandledrejection' ||
    typeof userAgent !== 'string' ||
    !/\bCriOS\/\d/.test(userAgent) ||
    !/\b(?:iPhone|iPad|iPod)\b/.test(userAgent)
  ) {
    return false;
  }

  return frames.every((frame) => {
    const paths = [frame.filename, frame.abs_path].filter((value) => value != null && value !== '');

    return (
      frame.function === 'promiseReactionJob' &&
      paths.length > 0 &&
      paths.every((value) => value === '[native code]') &&
      normalized(frame.module) === '' &&
      normalized(frame.package) === ''
    );
  });
}

export function isGoogleAdUndefinedPromiseRejection({
  breadcrumbs = [],
  exceptionType,
  exceptionValue,
  mechanism,
}: GoogleAdUndefinedPromiseRejectionInput): boolean {
  if (
    normalized(exceptionType) !== 'unhandledrejection' ||
    normalized(exceptionValue) !== GOOGLE_AD_UNDEFINED_REJECTION_VALUE ||
    normalized(mechanism) !== 'auto.browser.global_handlers.onunhandledrejection'
  ) {
    return false;
  }

  let adWasReady = false;

  return breadcrumbs.some((breadcrumb) => {
    if (
      normalized(breadcrumb.category) === 'ad_banner' &&
      normalized(breadcrumb.message) === GOOGLE_AD_READY_BREADCRUMB
    ) {
      adWasReady = true;
      return false;
    }

    return (
      adWasReady &&
      normalized(breadcrumb.category) === 'fetch' &&
      normalized(breadcrumb.level) === 'error' &&
      isGoogleAdRequest(breadcrumb.data?.url)
    );
  });
}

/**
 * Same decision as Sentry's `thirdPartyErrorFilterIntegration` with
 * `drop-error-if-contains-third-party-frames`: any located frame outside our bundles, such as a
 * browser extension, tag manager or inline script, makes the error third-party. Turbopack can
 * only provide that integration's build metadata by injecting it into every module.
 */
export function containsThirdPartyFrames(frames: readonly StackFrame[]): boolean {
  return frames.some(
    (frame) =>
      typeof frame.filename === 'string' &&
      (frame.lineno != null || frame.colno != null) &&
      !FIRST_PARTY_FRAME_FILENAME.test(frame.filename),
  );
}

export function isMapboxErrorCallbackTypeError({
  exceptionType,
  exceptionValue,
  frames = [],
}: StackErrorInput): boolean {
  return (
    normalized(exceptionType) === 'typeerror' &&
    normalized(exceptionValue) === MAPBOX_ERROR_CALLBACK_TYPE_ERROR_VALUE &&
    frames.some((frame) =>
      [frame.abs_path, frame.filename, frame.module, frame.package].some((value) =>
        normalized(value).includes(MAPBOX_FRAME_SIGNAL),
      ),
    )
  );
}

export function isMachineTranslatedDomRemovalError({
  breadcrumbs = [],
  exceptionType,
  exceptionValue,
  frames = [],
  pageIsTranslated = false,
}: TranslatedDomRemovalErrorInput): boolean {
  const hasTranslatedClickTarget = breadcrumbs.some(
    (breadcrumb) =>
      normalized(breadcrumb.category) === 'ui.click' &&
      normalized(breadcrumb.message).includes('font > font'),
  );
  const hasNativeRemoveChildFrame = frames.some(
    (frame) =>
      normalized(frame.function) === 'removechild' &&
      [frame.abs_path, frame.filename].some((value) => normalized(value) === '[native code]'),
  );
  // Turbopack chunk names are content hashes, so React's commit frames can only be
  // recognised as coming from our own bundles.
  const hasFirstPartyFrame = frames.some((frame) =>
    [frame.abs_path, frame.filename].some(
      (value) => typeof value === 'string' && FIRST_PARTY_FRAME_FILENAME.test(value),
    ),
  );
  const value = normalized(exceptionValue);
  // WebKit's message is generic, so it needs the native removeChild frame; Chromium's
  // message names removeChild itself and its stacks omit native frames.
  const isRemoveChildFailure =
    (value === TRANSLATED_DOM_REMOVAL_ERROR_VALUE && hasNativeRemoveChildFrame) ||
    value === CHROMIUM_DOM_REMOVAL_ERROR_VALUE;

  return (
    normalized(exceptionType) === 'notfounderror' &&
    isRemoveChildFailure &&
    (hasTranslatedClickTarget || pageIsTranslated) &&
    hasFirstPartyFrame
  );
}
